/**
 * Safe math expression evaluator
 * Supports: +, -, *, /, ^, sin, cos, tan, sqrt, abs, log, exp, pi, e
 */

const SAFE_MATH_FUNCS: Record<string, (...args: number[]) => number> = {
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  asin: Math.asin,
  acos: Math.acos,
  atan: Math.atan,
  sinh: Math.sinh,
  cosh: Math.cosh,
  tanh: Math.tanh,
  sqrt: Math.sqrt,
  cbrt: Math.cbrt,
  abs: Math.abs,
  log: Math.log10,
  ln: Math.log,
  exp: Math.exp,
  floor: Math.floor,
  ceil: Math.ceil,
  round: Math.round,
  sign: Math.sign,
  pow: Math.pow,
};

function preprocessExpression(expr: string): string {
  let e = expr.trim();
  // Remove "y = " or "f(x) = " prefix
  e = e.replace(/^[yY]\s*=\s*/, '');
  e = e.replace(/^[fFgGhH]\s*\([xX]\)\s*=\s*/, '');
  // Replace ^ with **
  e = e.replace(/\^/g, '**');
  // Replace implicit multiplication: 2x -> 2*x, 2(... -> 2*(...
  e = e.replace(/(\d)([a-zA-Z(])/g, '$1*$2');
  e = e.replace(/\)(\d)/g, ')*$1');
  e = e.replace(/\)([a-zA-Z(])/g, ')*$1');
  // Replace pi and e constants
  e = e.replace(/\bpi\b/gi, String(Math.PI));
  e = e.replace(/\bPI\b/g, String(Math.PI));
  e = e.replace(/\be\b/g, String(Math.E));
  return e;
}

export function parseAndEvaluate(expression: string, x: number): number {
  try {
    const expr = preprocessExpression(expression);
    // Build function body with safe math context
    const funcBody = `
      const sin = Math.sin, cos = Math.cos, tan = Math.tan,
            asin = Math.asin, acos = Math.acos, atan = Math.atan,
            sinh = Math.sinh, cosh = Math.cosh, tanh = Math.tanh,
            sqrt = Math.sqrt, cbrt = Math.cbrt, abs = Math.abs,
            log = Math.log10, ln = Math.log, exp = Math.exp,
            floor = Math.floor, ceil = Math.ceil, round = Math.round,
            sign = Math.sign, pow = Math.pow,
            pi = ${Math.PI}, PI = ${Math.PI}, e = ${Math.E};
      return (${expr});
    `;
    // eslint-disable-next-line no-new-func
    const fn = new Function('x', funcBody);
    const result = fn(x);
    if (typeof result !== 'number' || !isFinite(result) || isNaN(result)) {
      return NaN;
    }
    return result;
  } catch {
    return NaN;
  }
}

export function validateExpression(expression: string): string | null {
  if (!expression.trim()) return 'Expression cannot be empty';
  try {
    parseAndEvaluate(expression, 0);
    parseAndEvaluate(expression, 1);
    parseAndEvaluate(expression, -1);
    return null;
  } catch {
    return 'Invalid expression';
  }
}

export function computeGraphPoints(
  expression: string,
  xMin: number,
  xMax: number,
  steps = 500
): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];
  const step = (xMax - xMin) / steps;
  for (let i = 0; i <= steps; i++) {
    const x = xMin + i * step;
    const y = parseAndEvaluate(expression, x);
    points.push({ x, y });
  }
  return points;
}

// Get derivative via finite difference (for normal vector)
export function getDerivative(expression: string, x: number): number {
  const h = 1e-5;
  const y1 = parseAndEvaluate(expression, x - h);
  const y2 = parseAndEvaluate(expression, x + h);
  if (isNaN(y1) || isNaN(y2)) return 0;
  return (y2 - y1) / (2 * h);
}

export { SAFE_MATH_FUNCS };
